import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Mail, ChevronDown, ChevronUp, ExternalLink, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  unread: 'bg-blue-100 text-blue-700',
  read: 'bg-gray-100 text-gray-700',
  complete: 'bg-green-100 text-green-700',
};

const FILTERS = [
  { key: '', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'read', label: 'Read' },
  { key: 'complete', label: 'Complete' },
];


function FilterBar({ active, onChange }) {
  return (
    <div className="flex flex-wrap gap-2 mb-6">
      {FILTERS.map((f) => (
        <Badge
          key={f.key || 'all'}
          variant={active === f.key ? 'default' : 'outline'}
          className="cursor-pointer"
          onClick={() => onChange(f.key)}
          data-testid={`inbox-filter-${f.key || 'all'}`}
        >
          {f.label}
        </Badge>
      ))}
    </div>
  );
}


function OrderReference({ msg, orderIds }) {
  if (!msg.order_number) return null;
  const exists = orderIds.has(String(msg.order_number));
  return (
    <div className="text-xs flex items-center gap-2" data-testid={`msg-order-row-${msg.id}`}>
      <span className="text-muted-foreground">Referenced order:</span>
      {exists ? (
        <Link
          to={`/admin/orders?focus=${encodeURIComponent(msg.order_number)}`}
          className="inline-flex items-center gap-1 text-[hsl(210,45%,40%)] hover:underline font-mono"
          data-testid={`msg-order-link-${msg.id}`}
        >
          #{msg.order_number} <ExternalLink className="h-3 w-3" />
        </Link>
      ) : (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 font-mono"
          data-testid={`msg-order-not-found-${msg.id}`}
        >
          <AlertTriangle className="h-3 w-3" /> #{msg.order_number} — not found
        </span>
      )}
    </div>
  );
}


function StatusActions({ msg, onUpdate }) {
  const targets = ['unread', 'read', 'complete'].filter((s) => s !== msg.status);
  return (
    <div className="flex flex-wrap gap-2 pt-2 border-t">
      <span className="text-xs text-muted-foreground mr-1 self-center">Mark as:</span>
      {targets.map((status) => (
        <Button
          key={status}
          size="sm"
          variant="outline"
          className={`h-7 text-xs ${status === 'complete' ? 'text-green-600 border-green-300' : ''}`}
          onClick={(e) => onUpdate(msg.id, status, e)}
          data-testid={`mark-${status}-${msg.id}`}
        >
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Button>
      ))}
    </div>
  );
}


function MessageRow({ msg, expanded, onToggle, onUpdate, orderIds }) {
  return (
    <div className="border-b last:border-0" data-testid={`inbox-msg-${msg.id}`}>
      <button
        type="button"
        className="w-full text-left grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4 px-4 py-3 items-center hover:bg-muted/20"
        onClick={() => onToggle(msg.id)}
        data-testid={`expand-msg-${msg.id}`}
      >
        <span className="text-xs sm:text-sm text-muted-foreground">
          {new Date(msg.created_at).toLocaleDateString()}
        </span>
        <Badge className={`${STATUS_STYLES[msg.status] || 'bg-gray-100 text-gray-700'} text-xs w-fit`}>
          {msg.status}
        </Badge>
        <div className="text-right col-span-2 sm:col-span-1">
          {expanded
            ? <ChevronUp className="h-4 w-4 inline text-muted-foreground" />
            : <ChevronDown className="h-4 w-4 inline text-muted-foreground" />}
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-4 bg-muted/10 space-y-3">
          <div className="pt-2 space-y-0.5">
            <p className="text-sm font-medium text-foreground">{msg.first_name} {msg.last_name}</p>
            <p className="text-xs text-muted-foreground">{msg.email}</p>
          </div>
          <OrderReference msg={msg} orderIds={orderIds} />
          <p className="text-sm text-foreground font-body whitespace-pre-wrap">{msg.message}</p>
          <StatusActions msg={msg} onUpdate={onUpdate} />
        </div>
      )}
    </div>
  );
}


export default function AdminInboxPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [orderIds, setOrderIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState({});

  const fetchMessages = async (statusFilter) => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const { data } = await axios.get(`${API}/admin/inbox${params}`, { withCredentials: true });
      setMessages(data.messages);
    } catch {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'admin') { navigate('/'); return; }
    fetchMessages();
    // Fetch the list of all order IDs once so we can hyperlink-vs-flag the
    // order_number field on each message.
    axios.get(`${API}/admin/orders`, { withCredentials: true })
      .then(({ data }) => setOrderIds(new Set((data.orders || []).map((o) => String(o.id)))))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, navigate]);

  const updateStatus = async (id, status, e) => {
    e?.stopPropagation();
    try {
      await axios.patch(`${API}/admin/inbox/${id}`, { status }, { withCredentials: true });
      toast.success(`Marked as ${status}`);
      fetchMessages(filter);
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleFilter = (s) => { setFilter(s); setLoading(true); fetchMessages(s); };

  const toggleExpand = (id) => {
    setExpanded((p) => ({ ...p, [id]: !p[id] }));
    const msg = messages.find((m) => m.id === id);
    if (msg && msg.status === 'unread') {
      axios.patch(`${API}/admin/inbox/${id}`, { status: 'read' }, { withCredentials: true })
        .then(() => fetchMessages(filter))
        .catch(() => {});
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-16 bg-muted animate-pulse rounded mb-3" />
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="admin-inbox-page">
      <div className="mb-8">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[hsl(210,45%,40%)] mb-2 font-body">
          Admin
        </p>
        <h1 className="text-4xl tracking-tight font-light text-foreground font-heading">Inbox</h1>
        <p className="mt-2 text-base text-muted-foreground font-body">
          Messages from Contact Us form.
        </p>
      </div>

      <FilterBar active={filter} onChange={handleFilter} />

      {messages.length === 0 ? (
        <div className="text-center py-16" data-testid="inbox-empty">
          <Mail className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground font-body">No messages found.</p>
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="hidden sm:grid grid-cols-3 gap-4 px-4 py-3 bg-muted/50 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b">
              <span>Date</span><span>Status</span><span className="text-right">Expand</span>
            </div>
            {messages.map((msg) => (
              <MessageRow
                key={msg.id}
                msg={msg}
                expanded={!!expanded[msg.id]}
                onToggle={toggleExpand}
                onUpdate={updateStatus}
                orderIds={orderIds}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
